import React, { FC, useContext, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Button } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useInfiniteHits } from 'react-instantsearch';

import { UserInfoCtx } from '../../../../containers/CommonLayout/CommonLayout';
import { DiscoverContext } from '../../../../context/DiscoverContext';
import {
  GET_DISCOVER_PATHWAYS_DETAILED_INFO,
  GET_VIDEOS_EXIST_IN_USER_WATCH_LIST,
} from '../../../../graphql/pathways';
import { GET_VIDEO_PROGRESS_LIST } from '../../../../graphql/videos';
import useSERPNavigation from '../../../../hooks/useSERPNavigation';
import styled from '../../../../styled';
import {
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  SMALL_MARGIN,
  SMALL_MARGIN_PX,
  useIsWidthUp,
} from '../../../../theme';
import {
  Query,
  QueryGet_Discover_Pathways_Detailed_InfoArgs,
  QueryListVideoProgressArgs,
  QueryVideosExistInUserWatchListArgs,
} from '../../../../types';
import { collectValueByKey } from '../../../../utils/collectValueByKey';
import { HITS_TYPES } from '../../../const';
import type { AggregatorHit } from '../../const';
import { GlossaryGridHit } from '../GlossaryGridHit';
import { GlossaryInfiniteHist } from '../GlossaryInfiniteHits';
import GridHit from '../GridHit';
import { Hit } from '../Hit';
import LoadingHits from '../LoadingHits';

interface InfiniteHitsProps {
  tenantTitle?: string | null;
  tenantName?: string | null;
  page: number | null;
  limit: number;
  view: string;
  total: number | null;
  linkedInId?: string | null;
  loading: boolean;
}

export const InfiniteHits: FC<InfiniteHitsProps> = ({
  page,
  limit,
  total,
  view,
  tenantTitle,
  tenantName = null,
  loading,
}) => {
  const { t } = useTranslation();
  const isDesktop = useIsWidthUp('md');

  const isGridView = view === 'grid' && isDesktop;
  const showedItems = page ? (page + 1) * limit : limit;

  const serpType = useSERPNavigation();
  const onGlossaryPage = serpType === HITS_TYPES.GLOSSARY;
  const onVideoPage = serpType === HITS_TYPES.VIDEO;

  const { searchState } = useContext(DiscoverContext);
  const { userInfo } = useContext(UserInfoCtx);
  const { hits, isLastPage, showMore, sendEvent } = useInfiniteHits<AggregatorHit>();

  const [pathwayIds, setPathwayIds] = useState<number[]>([]);
  const [videoIds, setVideoIds] = useState<string[]>([]);

  const { data: pathwaysProgressData } = useQuery<
    Pick<Query, 'get_discover_pathways_detailed_info'>,
    QueryGet_Discover_Pathways_Detailed_InfoArgs
  >(GET_DISCOVER_PATHWAYS_DETAILED_INFO, {
    variables: {
      pathway_ids: pathwayIds,
    },
    skip: pathwayIds.length === 0 || !userInfo,
    fetchPolicy: 'network-only',
  });

  const pathwaysProgress = pathwaysProgressData?.get_discover_pathways_detailed_info || [];

  const { data: videoProgressData } = useQuery<
    Pick<Query, 'listVideoProgress'>,
    QueryListVideoProgressArgs
  >(GET_VIDEO_PROGRESS_LIST, {
    variables: {
      filter: {
        user_id: userInfo?.id || '',
        video_ids: videoIds,
      },
    },
    skip: !userInfo?.id || videoIds.length === 0 || isGridView,
    fetchPolicy: 'network-only',
  });

  const videosProgress = videoProgressData?.listVideoProgress || [];

  //fill video ids for all pages except non-video search type pages (pathways, insights, etc.)
  const videosIds =
    onVideoPage || !serpType
      ? hits
          ?.filter((entity) => entity?.searchable_type === HITS_TYPES.VIDEO)
          ?.map((entity) => entity?.id)
          ?.filter((item): item is number => Boolean(item))
          ?.map((item) => item.toString())
      : null;

  const { data: videosInWatchlist } = useQuery<
    Pick<Query, 'VideosExistInUserWatchList'>,
    QueryVideosExistInUserWatchListArgs
  >(GET_VIDEOS_EXIST_IN_USER_WATCH_LIST, {
    variables: {
      video_ids: videosIds,
    },
    skip: !videosIds || !videosIds?.length || !userInfo,
  });

  const watchlistById =
    videosInWatchlist?.VideosExistInUserWatchList?.reduce(
      collectValueByKey('video_id', 'exists'),
      {},
    ) || {};

  useEffect(() => {
    if (hits && hits.length > 0) {
      const newVideosIds = hits
        .filter((item) => item?.searchable_type === HITS_TYPES.VIDEO)
        .map((item) => item?.id?.toString());
      setVideoIds(newVideosIds);

      const newPathwaysIds = hits
        .filter((item) => item?.searchable_type === HITS_TYPES.PATHWAY)
        .map((item) => item.id);
      setPathwayIds(newPathwaysIds);
    }
  }, [hits]);

  return (
    <Wrapper>
      <ul
        className={cx(
          isGridView ? 'ais-InfiniteHits-grid' : 'ais-InfiniteHits-list',
          onGlossaryPage && 'ais-GlossaryInfiniteHits-list',
        )}
      >
        {loading && (
          <LoadingHits
            isGridView={isGridView}
            serpType={serpType}
            hasSearchTerm={searchState?.query}
          />
        )}
        {loading ? null : onGlossaryPage && !searchState?.query ? (
          <GlossaryInfiniteHist
            hits={hits}
            tenantTitle={tenantTitle}
            isGridView={isGridView}
            sendEvent={sendEvent}
          />
        ) : (
          hits.map((hit) => {
            const pathwayProgress = pathwaysProgress.find(
              (item) => item?.id === hit?.id && hit?.searchable_type === HITS_TYPES.PATHWAY,
            );
            const videoProgress = videosProgress.find(
              (item) =>
                item?.video?.id === hit?.id.toString() && hit?.searchable_type === HITS_TYPES.VIDEO,
            );

            return (
              <li data-testid="resultItem" key={hit.objectID}>
                {isGridView ? (
                  hit?.searchable_type === HITS_TYPES.GLOSSARY ? (
                    <GlossaryGridHit hit={hit} sendEvent={sendEvent} />
                  ) : (
                    <GridHit pathwayProgress={pathwayProgress} hit={hit} sendEvent={sendEvent} />
                  )
                ) : (
                  <Hit
                    hit={hit}
                    pathwayProgress={pathwayProgress}
                    videoProgress={videoProgress}
                    tenantTitle={tenantTitle}
                    tenantName={tenantName}
                    isInWatchlist={watchlistById[hit.id]}
                    sendEvent={sendEvent}
                  />
                )}
              </li>
            );
          })
        )}
      </ul>
      {!isLastPage && (
        <ViewBtn onClick={showMore}>
          {t('button.viewMore')} - {showedItems} {t('button.of')} {total}
        </ViewBtn>
      )}
    </Wrapper>
  );
};

export default InfiniteHits;

const Wrapper = styled('div')`
  & .ais-InfiniteHits-list {
    list-style-type: none;
    padding-left: 0;

    ${({ theme }) => theme.breakpoints.up('md')} {
      & > li {
        margin-bottom: 32px;
        padding-bottom: 32px;
        border-bottom: 1px solid ${({ theme }) => theme.palette.common.violetAnalyticsCard};

        &:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }
      }
    }
  }

  & .ais-InfiniteHits-list.ais-GlossaryInfiniteHits-list {
    & > li {
      margin-bottom: ${MIDDLE_MARGIN_PX};
      padding-bottom: 0;
      border-bottom: none;

      ${({ theme }) => theme.breakpoints.up('md')} {
        margin-bottom: ${LARGE_MARGIN_PX};
      }

      &:last-child {
        margin-bottom: ${MIDDLE_MARGIN_PX};

        ${({ theme }) => theme.breakpoints.up('md')} {
          margin-bottom: ${LARGE_MARGIN_PX};
        }
      }

      &.hit-letter {
        padding-bottom: ${SMALL_MARGIN / 2}px;
        border-bottom: 3px solid ${({ theme }) => theme.palette.common.violetAnalyticsCard};
      }
    }
  }

  & .ais-InfiniteHits-grid {
    list-style-type: none;
    padding-left: 0;
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    margin: 0;
    margin-right: -${SMALL_MARGIN_PX};

    & > li {
      margin-right: ${SMALL_MARGIN_PX};
      width: calc((100% - ${3 * SMALL_MARGIN}px) / 3);
    }
  }
`;
const ViewBtn = styled(Button)`
  width: 100%;
  text-transform: none;
  text-align: center;
  font-size: 18px;
  line-height: 1;
  padding: ${SMALL_MARGIN_PX};
  color: ${({ theme }) => theme.palette.common.gray};
  background: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  border-radius: 5px;
  font-weight: bold;
  @media (hover: none) {
    &:hover {
      background-color: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
    }
  }
`;
